import { find, findAll } from "./dom";

/**
 * The flag filter over the accommodation cards on /boende/ and the lake cabins' page.
 *
 * <p>Each card carries every flag it answers to in <code>data-flags</code>, joined with "|". A
 * pressed chip keeps only the cards that carry it; several pressed chips keep the cards that carry
 * all of them, because each one narrows the question ("pets" and "ground floor" means a room
 * with both). The count is announced in a live region, so a screen reader hears what the press
 * did without having to go looking.</p>
 *
 * <p>The row is rendered hidden and only shown here: without the script the chips would be
 * buttons that do nothing.</p>
 *
 * <p>The cards stand in groups, one per house. A group the filter leaves without a card goes
 * too, or its heading would stand over nothing.</p>
 */
export function initRoomFilter(): void {
  const filter = find<HTMLElement>("[data-room-filter]");
  if (filter === null) {
    return;
  }

  const chips = findAll<HTMLButtonElement>("button[data-flag]", filter);
  const status = find<HTMLElement>("[data-room-filter-status]", filter);
  const cards = findAll<HTMLElement>("[data-room-groups] [data-flags]");
  const groups = findAll<HTMLElement>("[data-room-group]");
  if (chips.length === 0 || cards.length === 0) {
    return;
  }

  const apply = (): void => {
    const wanted = chips
      .filter((chip) => chip.getAttribute("aria-pressed") === "true")
      .map((chip) => chip.dataset.flag ?? "");

    let shown = 0;
    for (const card of cards) {
      const flags = (card.dataset.flags ?? "").split("|");
      const match = wanted.every((flag) => flags.includes(flag));
      card.hidden = !match;
      if (match) {
        shown++;
      }
    }

    for (const group of groups) {
      group.hidden = find<HTMLElement>("[data-flags]:not([hidden])", group) === null;
    }

    if (status !== null) {
      status.textContent = wanted.length === 0
        ? ""
        : shown === 0
          ? "Inget boende har allt det valda. Ta bort ett val för att se fler."
          : `Visar ${shown} av ${cards.length} boenden.`;
    }
  };

  for (const chip of chips) {
    chip.addEventListener("click", () => {
      chip.setAttribute("aria-pressed", chip.getAttribute("aria-pressed") === "true" ? "false" : "true");
      apply();
    });
  }

  filter.hidden = false;
}
